import { IContactInfo } from './shared-panel-components/contact-card/contact-card.models';
import { ShelterProfileDataService } from './services/shelter-profile-data/shelter-profile-data.service';
import {
  ISidebar,
  ISidebarConfig,
  TSidebarEvent,
  IPanels,
  ILoadStudentConfig,
  IShelterProfileStudent,
} from './shelter-profile.models';
import { tap } from 'rxjs/operators';
import { ActivatedRoute, Params, Router, ActivatedRouteSnapshot } from '@angular/router';
import { SidebarEvents } from './shelter-profile.models';
import { SIDEBAR_EXPAND, SIDEBAR_COLLAPSE, HEADER_DATA_POINT_CONFIG, PANELS } from './shelter-profile.constants';
import { Component, OnInit } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { RouterExitService } from 'Src/ng2/routes/services/router-exit.service';

@Component({
  selector: 'shelter-profile',
  template: require('./shelter-profile.component.html'),
  styles: [require('./shelter-profile.component.scss')],
})
export class ShelterProfileComponent implements OnInit {
  public shelterId: string;
  public caresIds: Array<string>;

  public previousState: { url: string; queryParams: { [key: string]: string } };

  public currentStudent$ = new BehaviorSubject<IShelterProfileStudent>(null);
  public currentStudentIndex$ = new BehaviorSubject(0);

  public sidebar: ISidebar = SIDEBAR_COLLAPSE;
  public panels: IPanels = PANELS;

  public sidebarConfig: ISidebarConfig = { SIDEBAR_COLLAPSE, SIDEBAR_EXPAND };
  public headerDataPointsConfig = HEADER_DATA_POINT_CONFIG;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dataService: ShelterProfileDataService,
    private exitService: RouterExitService,
  ) {}

  ngOnInit(): void {
    this.shelterId = this.route.snapshot.params.shelterId;

    this.caresIds = this.route.snapshot.data.caresIds;

    this.previousState = this.exitService.previousUrl$.value || {
      url: `shelter/${this.shelterId}/lists/attendance-list`,
      queryParams: {},
    };

    const studentIndexToLoad: number = this.getCurrentStudentIndex(this.route.snapshot, this.caresIds);

    this.loadStudentData(studentIndexToLoad, {
      currentStudent$: this.currentStudent$,
      currentStudentIndex$: this.currentStudentIndex$,
      caresIds: this.caresIds,
      route: this.route,
      shelterId: this.shelterId,
    });
  }

  public onSidebarItemSelection(
    $evt: TSidebarEvent,
    sidebarData: ISidebar,
    panelData: IPanels,
    sidebarConfig: ISidebarConfig,
  ): void {
    // At this point we know we have to either toggle them all to expanded/collapsed
    this.panels = this.getResetPanels($evt, PANELS);
    // Update the sidebar state accordingly ie: collapse panels / expand panels
    this.sidebar = this.getSidebarState(sidebarData, panelData, sidebarConfig);
  }

  // A user has toggled an individual panel
  public onPanelStateChange(
    $event: { key: string; state: boolean },
    sidebarData: ISidebar,
    panelData: IPanels,
    sidebarConfig: ISidebarConfig,
  ): void {
    this.panels = this.getPanels($event, panelData);
    this.sidebar = this.getSidebarState(sidebarData, this.panels, sidebarConfig);
  }

  public openEmailForContact({ email }: IContactInfo): void {
    const permissions = navigator.permissions.query({ name: 'clipboard-write' as PermissionName });
    // Let's consider rollbar to get an idea of users permissions
    permissions.then(this.writeToClipboard.bind(this, email)).catch(console.warn);
  }

  public loadStudentData(
    index: number,
    { currentStudent$, currentStudentIndex$, caresIds, route, shelterId }: ILoadStudentConfig,
  ): void {
    // Check in with Product skeleton screen or other loading indicators besides a spinner.
    // Currently, we do not handle slow connections since even slow 3g feels ok
    this.dataService
      .getProfileData$(caresIds[index], shelterId)
      .pipe(
        tap(student => {
          currentStudent$.next(student);
          currentStudentIndex$.next(index);
          this.updateUrl(route, student.CARES_ID);
        }),
      )
      .subscribe();
  }

  public goToPreviousPage(previousState: { url: string; queryParams: { [key: string]: string } }): void {
    this.router.navigate([previousState.url], { queryParams: previousState.queryParams });
  }

  private getResetPanels($evt: TSidebarEvent, panelData: IPanels): IPanels {
    const expandedState = $evt === SidebarEvents.COLLAPSE_PANELS ? false : true;
    return Object.values(panelData).reduce((accum, v: any) => {
      accum[v.key] = { ...v, ...{ expanded: expandedState } };
      return accum;
    }, {});
  }

  private getPanels($evt: { key: string; state: boolean }, panelData: IPanels): IPanels {
    const { key, state: expandedState } = $evt;
    const updatedPanels = { ...panelData, ...{ [key]: { key, expanded: expandedState } } };
    return updatedPanels;
  }

  private writeToClipboard(email: string, { state }): string {
    const writableStates = ['granted', 'prompt'];
    if (writableStates.includes(state)) navigator.clipboard.writeText(email);
    return email;
  }

  // Based on the state of all panels we update the sidebar to all a user to quickly expand or collapse all panels
  private getSidebarState(sidebarData: ISidebar, panelData: IPanels, sidebarConfig: ISidebarConfig): ISidebar {
    const allCollapsed = Object.values(panelData).every(({ expanded }) => !expanded);
    const allExpanded = Object.values(panelData).every(({ expanded }) => expanded);
    if (allExpanded) return { ...sidebarConfig.SIDEBAR_COLLAPSE };
    if (allCollapsed) return { ...sidebarConfig.SIDEBAR_EXPAND };
    else return sidebarData;
  }

  private updateUrl(route: ActivatedRoute, currentId: string): void {
    const relativeTo = route;
    const queryParams: Params = { current: currentId };
    const queryParamsHandling = 'merge';
    const replaceUrl = true;
    this.router.navigate([], { relativeTo, queryParams, replaceUrl, queryParamsHandling });
  }

  private getCurrentStudentIndex(routeSnapshot: ActivatedRouteSnapshot, caresIds: Array<string>): number {
    const { current } = routeSnapshot.queryParams;
    const currentStudentIndex = caresIds.findIndex(id => id === current);
    return currentStudentIndex > -1 ? currentStudentIndex : 0;
  }
}
